import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { approvalService } from '@/services/approval.service';
import { formatApiError } from '@/services/api';
import { invalidateReleaseState } from '@/lib/invalidateRelease';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { toast } from '@/utils/toast';

interface WithdrawTarget {
  approvalId: number;
  postId: number;
}

/**
 * "Withdraw from review": take an item back out of review while it is still In review (sender / creator).
 * One implementation for the drawer and the board card menu: the call, the cache sync (the item leaves
 * the In review column, the composer unlocks) and the toasts. Resolves true when it worked; a refusal
 * (already released, changes requested, not allowed) shows the server's translated message and re-syncs,
 * because the item has moved on.
 */
export function useWithdrawFromReview(): { withdraw: (target: WithdrawTarget) => Promise<boolean>; isPending: boolean } {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  const mutation = useMutation({
    mutationFn: ({ approvalId }: WithdrawTarget) => approvalService.withdraw(approvalId),
    // Also on failure: a 409 means the item changed under us (released / sent back), so every surface re-reads it.
    onSettled: (_data, _error, variables) => {
      invalidateReleaseState(queryClient, { teamId, postId: variables.postId });
    },
  });

  const withdraw = useCallback(
    async (target: WithdrawTarget): Promise<boolean> => {
      try {
        await mutation.mutateAsync(target);
        toast.success(t('release_withdraw_success'));
        return true;
      } catch (error) {
        toast.error(formatApiError(error).message || t('release_withdraw_failed'));
        return false;
      }
    },
    [mutation, t]
  );

  return { withdraw, isPending: mutation.isPending };
}
